import { Check } from "lucide-react"

import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { cn } from "@/lib/utils"
import { BarButton, CountBadge } from "./FilterBarButton"
import MoreFilters from "./MoreFilters"
import TopicFilter from "./TopicFilter"

// The server labels roles in the singular for rows and chips; a tab names the
// group.
const TAB_LABELS = {
  writer: "Writers", editor: "Editors", designer: "Designers", illustrator: "Illustrators",
  narrator: "Narrators", translator: "Translators", humanize_ai: "Humanize AI",
}

const WHEN = [
  { key: "any", label: "Any time" },
  { key: "now", label: "Available now" },
  { key: "week", label: "Within a week" },
]

const MENU = "rounded-[13px] border-border bg-card p-[7px] shadow-[0_20px_46px_-16px_rgba(0,0,0,0.34)]"
const HEAD = "px-[11px] pt-[7px] pb-[6px] text-[10px] font-bold tracking-[0.09em] text-ink-3 uppercase"
const ITEM = "gap-[11px] rounded-[9px] px-[11px] py-[10px] text-[13.5px] [&>span:first-child]:hidden"

// Role tabs. Arrow keys move focus without searching; Enter or a click picks,
// so stepping along the row does not fire a search per tab.
function RoleTabs({ roles, value, onPick }) {
  return (
    <Tabs value={value || "all"} onValueChange={(v) => onPick(v === "all" ? null : v)} activationMode="manual">
      <TabsList
        aria-label="Role"
        className="h-auto w-full justify-start gap-[4px] overflow-x-auto rounded-none border-b border-border bg-transparent p-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {[{ key: "all", label: "All" }, ...roles].map((r) => (
          <TabsTrigger
            key={r.key}
            value={r.key}
            className="group -mb-px h-auto flex-none gap-[7px] rounded-none border-0 border-b-2 border-transparent px-[12px] pt-[10px] pb-[12px] text-[13.5px] font-semibold text-ink-3 shadow-none hover:text-foreground data-[state=active]:border-foreground data-[state=active]:bg-transparent data-[state=active]:text-foreground data-[state=active]:shadow-none dark:text-ink-3 dark:data-[state=active]:border-foreground dark:data-[state=active]:bg-transparent"
          >
            {TAB_LABELS[r.key] || r.label}
          </TabsTrigger>
        ))}
      </TabsList>
    </Tabs>
  )
}

// "Can start" (one choice) plus two status switches: the four availability
// controls the rail used to show separately. Ticking a status keeps the menu
// open so both can be set in one visit.
function AvailabilityFilter({ applied, onChange }) {
  const n = (applied.availability ? 1 : 0) + (applied.online ? 1 : 0) + (applied.noActive ? 1 : 0)
  const label =
    n !== 1 ? "Availability"
      : applied.online ? "Online now"
        : applied.noActive ? "No active orders"
          : WHEN.find((w) => w.key === applied.availability)?.label
  const keepOpen = (e) => e.preventDefault()
  const box = (on) => (
    <span
      className={cn(
        "flex size-[16px] shrink-0 items-center justify-center rounded-[4px] border-[1.5px]",
        on ? "border-foreground bg-foreground text-background" : "border-line-2 text-transparent"
      )}
    >
      <Check className="size-[11px] text-current" strokeWidth={3} />
    </span>
  )

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <BarButton has={n > 0}>
          <span className="max-w-[170px] truncate max-[561px]:max-w-full">{label}</span>
          {n > 1 && <CountBadge n={n} />}
        </BarButton>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" sideOffset={7} collisionPadding={14} className={cn(MENU, "min-w-[250px]")}>
        <DropdownMenuLabel className={HEAD}>Can start</DropdownMenuLabel>
        <DropdownMenuRadioGroup
          value={applied.availability || "any"}
          onValueChange={(v) => onChange({ availability: v === "any" ? null : v })}
        >
          {WHEN.map((w) => {
            const on = (applied.availability || "any") === w.key
            return (
              <DropdownMenuRadioItem key={w.key} value={w.key} className={cn(ITEM, on ? "bg-accent font-bold" : "font-medium")}>
                <span className="flex-1">{w.label}</span>
                {on && <Check className="size-4 text-foreground" strokeWidth={2.6} />}
              </DropdownMenuRadioItem>
            )
          })}
        </DropdownMenuRadioGroup>
        <DropdownMenuSeparator className="mx-[4px] my-[6px]" />
        <DropdownMenuLabel className={HEAD}>Status</DropdownMenuLabel>
        <DropdownMenuCheckboxItem
          checked={applied.online}
          onSelect={keepOpen}
          onCheckedChange={(v) => onChange({ online: v })}
          className={cn(ITEM, applied.online ? "font-semibold" : "font-medium")}
        >
          {box(applied.online)}
          <span className="-mr-[4px] size-2 rounded-full bg-done" />
          Online now
        </DropdownMenuCheckboxItem>
        <DropdownMenuCheckboxItem
          checked={applied.noActive}
          onSelect={keepOpen}
          onCheckedChange={(v) => onChange({ noActive: v })}
          className={cn(ITEM, applied.noActive ? "font-semibold" : "font-medium")}
        >
          {box(applied.noActive)}
          No active orders
          <span className="ml-auto text-[11.5px] font-medium text-ink-3">free to start</span>
        </DropdownMenuCheckboxItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

// Find Freelancers' filter bar: role tabs over one row of search, Topic,
// Availability and More filters. Stateless about results — it renders from the
// server's `facets` and validated `applied` state and emits a patch through
// onChange, which the page turns into a partial reload. `search` is the page's
// own debounced search field.
export default function FilterBar({ facets, applied, total, onChange, search, guest = false }) {
  return (
    <div className="flex flex-col gap-[14px]">
      <RoleTabs roles={facets.roles} value={applied.role} onPick={(role) => onChange({ role })} />
      <div className="flex items-center gap-[10px] max-[561px]:flex-wrap">
        {search}
        <TopicFilter niches={facets.niches} applied={applied.niches ?? []} onChange={onChange} />
        <AvailabilityFilter applied={applied} onChange={onChange} />
        <MoreFilters facets={facets} applied={applied} total={total} onChange={onChange} guest={guest} />
      </div>
    </div>
  )
}
